import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Icon,
  Text,
} from "@highoutput/ui-components";
import type { FaqCoreDataFragment } from "~/__generated__/graphql";
import Calendly from "~/components/calendly";
import For from "../../../utilities/for";
import { LargeBreak, MediumBreak } from "../helpers";

type FaqsProps = {
  data: FaqCoreDataFragment[];
};

export default function Faqs({ data }: FaqsProps) {
  return (
    <Box
      px={{ xl: "160px", lg: "24px", base: "16px" }}
      mx="auto"
      zIndex={1}
      bg="white"
      py={{
        base: "64px",
        lg: "100px",
        xl: "100px",
      }}
      maxWidth={"1440px"}
      fontFamily="'Inter', sans-serif"
    >
      <Flex
        gap={{
          lg: "80px",
        }}
        direction={"column"}
        align={"center"}
      >
        <Flex align="center" direction="column" fontFamily="'Inter', sans-serif">
          <Text
            mb={{ lg: "12px", base: "5px" }}
            color="rgba(13, 48, 80, 0.8)"
            textTransform={"uppercase"}
            letterSpacing={"0.05em"}
            w="full"
            textAlign={"center"}
            fontSize={{ lg: "18px", base: "14px" }}
            lineHeight={{ lg: "18px", base: "14px" }}
          >
            support
          </Text>

          <Text
            mb={{ lg: "16px", base: "16px" }}
            color="#0D3050"
            letterSpacing={"-0.02em"}
            w="full"
            textAlign={"center"}
            fontSize={{ lg: "40px", base: "32px" }}
            lineHeight={{ lg: "44px", base: "36px" }}
            fontFamily={"Georgia"}
          >
            Frequently Asked Questions
          </Text>

          <Text
            color="rgba(13, 48, 80, 0.8)"
            fontSize={{ lg: "18px", base: "14px" }}
            lineHeight={{ lg: "28px", base: "20px" }}
            maxWidth={{ lg: "821px" }}
            textAlign={"center"}
            letterSpacing={{ base: "0.02em" }}
          >
            Everything you need to know about the product and billing. <LargeBreak />{" "}
            <MediumBreak /> Can’t find the answer you’re looking for? Please{" "}
            <Calendly textBtn>
              <Text as="span" cursor={"pointer"}>
                schedule a call
              </Text>
            </Calendly>{" "}
            to our friendly Advante team.
          </Text>
        </Flex>

        <Flex direction={"column"} mt={{ base: 6, lg: 0 }} w="full">
          <Accordion allowToggle>
            <For each={data}>
              {({ question, answer, id }) => (
                <AccordionItem
                  p={{ xl: "40px", lg: "32px", base: "24px" }}
                  border="1px solid rgba(240, 237, 236, 0.8)"
                  mb={"24px"}
                  borderRadius={"8px"}
                >
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton
                        display="flex"
                        gap="24px"
                        justifyContent="space-between"
                        fontSize="inherit"
                        alignItems={"start"}
                        lineHeight="inherit"
                        textAlign="unset"
                        padding={0}
                        _hover={{
                          bgColor: "unset",
                        }}
                      >
                        <Text
                          fontWeight="600"
                          data-testid={`faq.${id}.question`}
                          color="#0D3050"
                          fontSize={{ lg: "18px", base: "14px" }}
                          lineHeight={{ lg: "28px", base: "20px" }}
                          letterSpacing={{ base: "0.02em" }}
                        >
                          {question}
                        </Text>
                        <Icon
                          as={isExpanded ? MinusIcon : PlusIcon}
                          height="24px"
                          width="24px"
                          color="rgba(13, 48, 80, 0.8)"
                        />
                      </AccordionButton>

                      <AccordionPanel
                        color="rgba(13,48,80,0.6)"
                        padding={0}
                        marginTop="12px"
                        display="flex"
                      >
                        <Text
                          fontSize={{ lg: "18px", base: "12px" }}
                          lineHeight={{ lg: "28px", base: "16px" }}
                          color="rgba(13, 48, 80, 0.8)"
                          data-testid={`faq.${id}.answer`}
                          letterSpacing={{ base: "0.02em" }}
                          w={{ xl: "935px", lg: "830px", base: "239px" }}
                        >
                          {answer}
                        </Text>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              )}
            </For>
          </Accordion>
        </Flex>
      </Flex>
    </Box>
  );
}
